import { ApprovalResponseNotification } from '@agilelab/plugin-wb-notification-common';
import { WbAvatar } from '@agilelab/plugin-wb-platform';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { Box, Typography, useTheme } from '@material-ui/core';
import DOMPurify from 'dompurify';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { MarkAsReadButton } from './MarkAsReadButton';
import {
  NotificationDisclosure,
  NotificationDisclosureSummary,
} from './NotificationDisclosure';
import { useNotificationStyle } from './NotificationsPageStyle';
import { useNotification } from './useNotificationProvider';
import logo from './witboost_logo.svg';

export type ApprovalRespondedData = {
  type: 'approval-responded';
  workflowInstanceId: string;
  entityUrn: string;
  entityDisplayName: string;
  resolution: 'approved' | 'rejected';
  resolvedBy: string;
  requesterRef?: string;
  environment?: string;
  domain?: string;
  releaseVersion?: string;
};

export function isApprovalRespondedData(
  json: unknown,
): json is ApprovalRespondedData {
  if (!json || typeof json !== 'object') return false;
  const obj = json as Record<string, unknown>;
  return (
    obj.type === 'approval-responded' &&
    typeof obj.workflowInstanceId === 'string' &&
    typeof obj.entityUrn === 'string'
  );
}

export const ApprovalRespondedNotificationDisclosure = (props: {
  notification: ApprovalResponseNotification;
  onAccordionExpansionsChange: (value: string | boolean) => void;
  expandedAccordion: string | boolean;
}) => {
  const { notification, onAccordionExpansionsChange, expandedAccordion } =
    props;
  const classes = useNotificationStyle();
  const theme = useTheme();
  const configApi = useApi(configApiRef);
  const { setRead } = useNotification();

  const respondedData = isApprovalRespondedData(
    notification.notification_request.json,
  )
    ? (notification.notification_request.json as ApprovalRespondedData)
    : undefined;

  const resolution = respondedData?.resolution;

  const onToggle = useCallback(
    (_event: object, expanded: boolean) => {
      if (expanded && !notification.read_at) {
        setRead(notification.id.toString());
      }
      onAccordionExpansionsChange(
        expanded ? notification.id.toString() : false,
      );
    },
    [
      notification.id,
      notification.read_at,
      onAccordionExpansionsChange,
      setRead,
    ],
  );

  return (
    <NotificationDisclosure
      onChange={onToggle}
      expanded={expandedAccordion === notification.id.toString()}
      style={{
        backgroundColor: notification.read_at
          ? 'transparent'
          : theme.palette.common.white,
      }}
    >
      <NotificationDisclosureSummary expandIcon={<ExpandMoreIcon />}>
        <div className={classes.summary}>
          <Box>
            <div className={classes.summaryHeader}>
              <Typography className={classes.notificationTime}>
                {DateTime.fromISO(notification.created_at).toFormat(
                  'dd/MM/yyyy HH:mm',
                )}
              </Typography>
              {resolution === 'approved' && (
                <CheckCircleOutlineOutlinedIcon
                  fontSize="small"
                  style={{ color: theme.palette.success.main }}
                />
              )}
              {resolution === 'rejected' && (
                <NotInterestedIcon
                  fontSize="small"
                  style={{ color: theme.palette.error.main }}
                />
              )}
            </div>
            <div className={classes.summaryDescription}>
              <WbAvatar
                displayName={
                  notification.sender ??
                  configApi.getOptionalString('app.title') ??
                  'Witboost'
                }
                picture={logo}
                classes={{ avatar: classes.avatar }}
              />
              <div className={classes.summaryDescriptionText}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      notification.notification_request.text,
                    ),
                  }}
                />
              </div>
            </div>
          </Box>
          <Box alignSelf="center">
            <MarkAsReadButton notification={notification} />
          </Box>
        </div>
      </NotificationDisclosureSummary>
    </NotificationDisclosure>
  );
};
